import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { getAuthHeaders } from '../lib/auth';
import Navigation from '../components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DURATIONS = [15, 30, 45, 60, 90, 120];
const LOCATIONS = [
  { value: 'zoom', label: 'Zoom' },
  { value: 'google_meet', label: 'Google Meet' },
  { value: 'teams', label: 'Microsoft Teams' },
  { value: 'phone', label: 'Phone Call' },
  { value: 'in_person', label: 'In Person' },
  { value: 'custom', label: 'Custom' }
];

const QUESTION_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Multiple Choice' },
  { value: 'checkbox', label: 'Checkboxes' }
];

const EventTypeForm = () => {
  const navigate = useNavigate();
  const { eventTypeId } = useParams();
  const isEditMode = Boolean(eventTypeId);

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration: 30,
    location: 'zoom',
    locationDetails: '',
    color: '#3b82f6',
    useUserAvailability: true,
    isSecret: false,
    confirmationMessage: 'Your meeting has been scheduled!',
    customQuestions: [],
    schedulingRules: {
      minimumNotice: 0,
      dateRangeDays: 60,
      dailyLimit: null,
      bufferTime: {
        before: 0,
        after: 0
      },
      startTimeIncrements: 15
    }
  });

  useEffect(() => {
    if (isEditMode) {
      loadEventType();
    }
  }, [eventTypeId]);

  const loadEventType = async () => {
    try {
      const response = await axios.get(`${API}/event-types/${eventTypeId}`, {
        headers: getAuthHeaders()
      });
      const eventType = response.data.eventType;
      setFormData({
        name: eventType.name || '',
        description: eventType.description || '',
        duration: eventType.duration || 30,
        location: eventType.location || 'zoom',
        locationDetails: eventType.locationDetails || '',
        color: eventType.color || '#3b82f6',
        useUserAvailability: eventType.useUserAvailability !== false,
        isSecret: eventType.isSecret || false,
        confirmationMessage: eventType.confirmationMessage || 'Your meeting has been scheduled!',
        customQuestions: eventType.customQuestions || [],
        schedulingRules: eventType.schedulingRules || {
          minimumNotice: 0,
          dateRangeDays: 60,
          dailyLimit: null,
          bufferTime: { before: 0, after: 0 },
          startTimeIncrements: 15
        }
      });
    } catch (error) {
      toast.error('Failed to load event type');
      navigate('/event-types');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSchedulingRuleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      schedulingRules: {
        ...prev.schedulingRules,
        [field]: value
      }
    }));
  };

  const handleBufferTimeChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      schedulingRules: {
        ...prev.schedulingRules,
        bufferTime: {
          ...prev.schedulingRules.bufferTime,
          [field]: parseInt(value) || 0
        }
      }
    }));
  };

  const addCustomQuestion = () => {
    setFormData(prev => ({
      ...prev,
      customQuestions: [
        ...prev.customQuestions,
        {
          id: `q-${Date.now()}`,
          question: '',
          type: 'text',
          required: true,
          options: []
        }
      ]
    }));
  };

  const removeCustomQuestion = (index) => {
    setFormData(prev => ({
      ...prev,
      customQuestions: prev.customQuestions.filter((_, i) => i !== index)
    }));
  };

  const updateCustomQuestion = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      customQuestions: prev.customQuestions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEditMode) {
        await axios.put(`${API}/event-types/${eventTypeId}`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Event type updated!');
      } else {
        await axios.post(`${API}/event-types`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Event type created!');
      }
      navigate('/event-types');
    } catch (error) {
      toast.error(isEditMode ? 'Failed to update event type' : 'Failed to create event type');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
            <p className="mt-4 text-slate-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/event-types')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Event Types
        </Button>

        <h1 className="text-4xl font-bold text-slate-900 mb-8" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
          {isEditMode ? 'Edit Event Type' : 'Create Event Type'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Event Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g., 30-Minute Consultation"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="What can invitees expect from this meeting?"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="duration">Duration (minutes) *</Label>
                  <Select value={formData.duration.toString()} onValueChange={(v) => handleChange('duration', parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DURATIONS.map(d => (
                        <SelectItem key={d} value={d.toString()}>{d} min</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="color">Color</Label>
                  <Input
                    id="color"
                    type="color"
                    value={formData.color}
                    onChange={(e) => handleChange('color', e.target.value)}
                    className="h-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="location">Location *</Label>
                <Select value={formData.location} onValueChange={(v) => handleChange('location', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATIONS.map(loc => (
                      <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {(formData.location === 'in_person' || formData.location === 'custom' || formData.location === 'phone') && (
                <div>
                  <Label htmlFor="locationDetails">Location Details</Label>
                  <Input
                    id="locationDetails"
                    value={formData.locationDetails}
                    onChange={(e) => handleChange('locationDetails', e.target.value)}
                    placeholder={formData.location === 'phone' ? 'Enter phone number' : 'Enter location or instructions'}
                  />
                </div>
              )}
            </div>
          </Card>

          {/* Scheduling Rules */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Scheduling Rules</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="minimumNotice">Minimum Notice (hours)</Label>
                  <Input
                    id="minimumNotice"
                    type="number"
                    min="0"
                    value={formData.schedulingRules.minimumNotice}
                    onChange={(e) => handleSchedulingRuleChange('minimumNotice', parseInt(e.target.value) || 0)}
                    placeholder="0"
                  />
                  <p className="text-sm text-slate-500 mt-1">Minimum hours before booking</p>
                </div>

                <div>
                  <Label htmlFor="dateRangeDays">Date Range (days)</Label>
                  <Input
                    id="dateRangeDays"
                    type="number"
                    min="1"
                    value={formData.schedulingRules.dateRangeDays}
                    onChange={(e) => handleSchedulingRuleChange('dateRangeDays', parseInt(e.target.value) || 60)}
                    placeholder="60"
                  />
                  <p className="text-sm text-slate-500 mt-1">How far ahead can book</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="dailyLimit">Daily Limit</Label>
                  <Input
                    id="dailyLimit"
                    type="number"
                    min="1"
                    value={formData.schedulingRules.dailyLimit || ''}
                    onChange={(e) => handleSchedulingRuleChange('dailyLimit', e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="Unlimited"
                  />
                  <p className="text-sm text-slate-500 mt-1">Max meetings/day</p>
                </div>

                <div>
                  <Label htmlFor="bufferBefore">Buffer Before (min)</Label>
                  <Input
                    id="bufferBefore"
                    type="number"
                    min="0"
                    value={formData.schedulingRules.bufferTime.before}
                    onChange={(e) => handleBufferTimeChange('before', e.target.value)}
                    placeholder="0"
                  />
                </div>

                <div>
                  <Label htmlFor="bufferAfter">Buffer After (min)</Label>
                  <Input
                    id="bufferAfter"
                    type="number"
                    min="0"
                    value={formData.schedulingRules.bufferTime.after}
                    onChange={(e) => handleBufferTimeChange('after', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Custom Questions */}
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Custom Questions</h2>
              <Button type="button" onClick={addCustomQuestion} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Question
              </Button>
            </div>

            {formData.customQuestions.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No custom questions yet. Add questions to collect information from invitees.</p>
            ) : (
              <div className="space-y-4">
                {formData.customQuestions.map((question, index) => (
                  <div key={question.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-sm font-medium text-slate-700">Question {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeCustomQuestion(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <Label>Question Text *</Label>
                        <Input
                          value={question.question}
                          onChange={(e) => updateCustomQuestion(index, 'question', e.target.value)}
                          placeholder="What would you like to ask?"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Type</Label>
                          <Select
                            value={question.type}
                            onValueChange={(v) => updateCustomQuestion(index, 'type', v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {QUESTION_TYPES.map(type => (
                                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="flex items-center gap-2 pt-6">
                          <input
                            type="checkbox"
                            id={`required-${index}`}
                            checked={question.required}
                            onChange={(e) => updateCustomQuestion(index, 'required', e.target.checked)}
                            className="rounded"
                          />
                          <Label htmlFor={`required-${index}`} className="cursor-pointer">Required</Label>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Confirmation */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Confirmation</h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="confirmationMessage">Confirmation Message</Label>
                <Textarea
                  id="confirmationMessage"
                  value={formData.confirmationMessage}
                  onChange={(e) => handleChange('confirmationMessage', e.target.value)}
                  placeholder="Message shown after booking"
                  rows={3}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isSecret"
                  checked={formData.isSecret}
                  onChange={(e) => handleChange('isSecret', e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="isSecret" className="cursor-pointer">
                  Secret Event (only accessible via direct link)
                </Label>
              </div>
            </div>
          </Card>

          {/* Submit */}
          <div className="flex gap-4">
            <Button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700">
              {saving ? 'Saving...' : isEditMode ? 'Update Event Type' : 'Create Event Type'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate('/event-types')}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventTypeForm;